import { Paragraph } from '@uncinc/react-drupal-core';
import type { ParagraphEntity, ReferenceField } from '@uncinc/react-drupal-core/types';
import classNames from 'classnames';
import Immutable from 'immutable';
import React, { useMemo } from 'react';

import isAudioParagraph from '../../helpers/isAudioParagraph';

declare interface ParagraphsRenderProps extends Record<string, any> {
  paragraphs: ReferenceField<ParagraphEntity>[];
  className?: string;
}

interface ParagraphsExclude extends ParagraphsRenderProps {
  exclude?: string[];
  only?: never;
}
interface ParagraphsOnly extends ParagraphsRenderProps {
  exclude?: never;
  only?: string[];
}


const ParagraphsRenderer: React.FC<ParagraphsExclude | ParagraphsOnly> = ({
  paragraphs,
  className,
  exclude = [],
  only = [],
  ...restProps
}) => {
  const paragraphsFiltered = useMemo(() => {
    const isImmutable = Immutable.isImmutable(paragraphs);
    const paragraphsArray = isImmutable
      ? (paragraphs.toJS() as ReferenceField<ParagraphEntity>[])
      : paragraphs;

    return paragraphsArray.filter((paragraph) => {
      const paragraphType = paragraph.entity.type[0].target_id;

      if (only.length) {
        return only.includes(paragraphType);
      }

      if (exclude.length) {
        return !exclude.includes(paragraphType);
      }

      return true;
    });
  }, [exclude, only, paragraphs]);

  const groupedParagraphs = useMemo(() => {
    if (!paragraphsFiltered?.length) {
      return [];
    }

    return paragraphsFiltered.reduce<ReferenceField<ParagraphEntity>[][]>(
      (acc, paragraph) => {
        const lastGroup = acc.at(-1);
        const lastParagraph = lastGroup?.at(-1);

        if (
          lastGroup && lastParagraph && ((
            isAudioParagraph(lastParagraph.entity) &&
            isAudioParagraph(paragraph.entity)
          ) || (
            lastParagraph.entity.type[0].target_id === 'text_media' &&
            paragraph.entity.type[0].target_id === 'text_media'
          ))
        ) {
          lastGroup.push(paragraph);
        } else {
          acc.push([paragraph]);
        }
        return acc;
      },
      [],
    );
  }, [paragraphsFiltered]);

  if (!groupedParagraphs.length) return null;

  const classes = classNames('Paragraphs', className);

  return (
    <div className={classes}>
      {groupedParagraphs.map((group) => {
        if (group.length > 1 && group[0]) {
          const groupType = group[0].entity.type[0].target_id;
          const groupId = group[0].entity.id[0].value;
          const groupClassName = classNames('Paragraphs__group', `Paragraphs__group--${groupType}`);

          return (
            <div
              key={`group-${groupType}-${groupId}`}
              className={groupClassName}
            >
              {group.map((paragraph) => {
                const id = paragraph.target_id;
                const language = paragraph.entity?.langcode[0]?.value;

                return (
                  <Paragraph
                    {...restProps}
                    key={`paragraph:${id}:${language}`}
                    id={id}
                    entity={Immutable.fromJS(paragraph.entity)}
                  />
                );
              })}
            </div>
          );
        }

        const paragraph = group[0];
        if (!paragraph) {
          return null;
        }

        const id = paragraph.target_id;
        const language = paragraph.entity?.langcode[0]?.value;

        return (
          <Paragraph
            {...restProps}
            key={`paragraph:${id}:${language}`}
            id={id}
            entity={Immutable.fromJS(paragraph.entity)}
          />
        );
      })}
    </div>
  );
};

export default ParagraphsRenderer;
